import { Link } from "react-router-dom";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { motion } from "motion/react";

export default function BlogSection() {
  const { posts, loading } = useBlogPosts();

  const recentPosts = posts.slice(0, 3);

  return (
    <section className="w-full py-16 md:py-20 px-4 md:px-6 bg-white">
      <div className="max-w-[1280px] mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
          className="flex items-end justify-between mb-8"
        >
          <div>
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-article-line text-[#C4956A] text-sm"></i>
              <span className="text-xs font-medium text-[#C4956A]">블로그</span>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-[#1C1917]">
              경매 투자 인사이트
            </h2>
            <p className="text-sm text-[#8C8780] mt-1">
              강스터디 전문가들의 최신 경매 노하우
            </p>
          </div>
          <Link
            to="/blog"
            className="hidden sm:flex items-center gap-1.5 text-sm font-medium text-[#C4956A] hover:text-[#B08050] transition-colors whitespace-nowrap"
          >
            전체보기
            <i className="ri-arrow-right-line"></i>
          </Link>
        </motion.div>

        {/* Posts Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-[#FAFAF8] rounded-2xl overflow-hidden animate-pulse">
                <div className="aspect-[16/10] bg-[#E8E5E0]"></div>
                <div className="p-5 space-y-3">
                  <div className="h-3 bg-[#E8E5E0] rounded-full w-1/3"></div>
                  <div className="h-4 bg-[#E8E5E0] rounded-full w-full"></div>
                  <div className="h-4 bg-[#E8E5E0] rounded-full w-2/3"></div>
                  <div className="h-3 bg-[#E8E5E0] rounded-full w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {recentPosts.map((post, idx) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: idx * 0.1, duration: 0.45, ease: [0.25, 0.1, 0.25, 1] as const }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="block bg-white rounded-2xl border border-[#E8E5E0] overflow-hidden group hover:border-[#C4956A]/30 transition-all"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img loading="lazy" decoding="async"
                      src={post.thumbnail}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 text-xs font-medium bg-[#1C1917]/80 text-white rounded-full backdrop-blur-sm">
                        {post.categoryLabel}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-sm font-bold text-[#1C1917] mb-2 line-clamp-2 group-hover:text-[#C4956A] transition-colors leading-snug">
                      {post.title}
                    </h3>
                    <p className="text-xs text-[#8C8780] line-clamp-2 mb-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-xs text-[#B8B3AE]">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full overflow-hidden border border-[#E8E5E0]">
                          <img loading="lazy" decoding="async" src={post.authorAvatar} alt={post.author} className="w-full h-full object-cover" />
                        </div>
                        <span>{post.author}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span>{post.createdAt}</span>
                        <span>·</span>
                        <span>{post.readTime}분</span>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* Mobile "View All" */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-6 text-center sm:hidden"
        >
          <Link
            to="/blog"
            className="inline-flex items-center gap-1.5 px-6 py-3 text-sm font-medium text-[#C4956A] border border-[#C4956A]/30 rounded-full hover:bg-[#C4956A]/5 transition-all"
          >
            블로그 전체보기
            <i className="ri-arrow-right-line"></i>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}